/* Welcome-music auto-player for the portal landing.
 *
 * Per STAS's directive, the client portal does NOT expose any mute/toggle
 * button. The music simply begins on the FIRST user interaction with the
 * landing page (required by every modern browser's autoplay policy) and
 * loops in the background until the user navigates away.
 *
 * Visible component renders nothing — it is purely a controller.
 */
import { useEffect, useRef } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PortalWelcomeMusic() {
  const audioRef = useRef(null);
  const startedRef = useRef(false);
  const cfgRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const ensureAudio = () => {
      const cfg = cfgRef.current;
      if (!cfg?.enabled || !cfg?.track?.url) return null;
      if (!audioRef.current) {
        const a = new Audio(cfg.track.url);
        a.loop = true;
        a.volume = Math.max(0, Math.min(1, cfg.volume ?? 0.4));
        a.crossOrigin = "anonymous";
        audioRef.current = a;
      } else {
        // Settings changed — keep the same track if URL matches, otherwise swap.
        if (audioRef.current.src !== cfg.track.url) {
          audioRef.current.pause();
          audioRef.current.src = cfg.track.url;
        }
        audioRef.current.volume = Math.max(0, Math.min(1, cfg.volume ?? 0.4));
      }
      return audioRef.current;
    };

    const tryStart = () => {
      const a = ensureAudio();
      if (!a) return;
      a.play().then(() => { startedRef.current = true; }).catch(() => {
        // Browser still blocks — will retry on the next user interaction.
      });
    };

    // Once the user interacts anywhere, start the music. Capture once.
    const onInteract = () => {
      if (!startedRef.current) tryStart();
      if (startedRef.current) {
        window.removeEventListener("pointerdown", onInteract, true);
        window.removeEventListener("keydown", onInteract, true);
        window.removeEventListener("touchstart", onInteract, true);
      }
    };
    window.addEventListener("pointerdown", onInteract, true);
    window.addEventListener("keydown", onInteract, true);
    window.addEventListener("touchstart", onInteract, true);

    const load = () =>
      axios.get(`${API_BASE}/api/portal/music`).then(({ data }) => {
        if (cancelled) return;
        cfgRef.current = data;
        // If we've already had interaction and a track was just enabled, kick it off.
        if (startedRef.current === false) return;
        const a = ensureAudio();
        if (a && a.paused) a.play().catch(() => {});
      }).catch(() => { /* music is non-critical */ });

    load();
    const t = setInterval(load, 90000);

    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("pointerdown", onInteract, true);
      window.removeEventListener("keydown", onInteract, true);
      window.removeEventListener("touchstart", onInteract, true);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return null;
}
